import React, {useRef} from 'react'
import Link from 'next/link'
import {useRouter} from 'next/router'

import {createLog} from '@util/logs'
import {QueryCacheKeys} from '@hook/query/queryKeys'
import {getLinkUrl, URL_ACCOUNT_API, URL_TIMELINE_USER} from '@constant/url'

import {useTranslation} from '@lib/intl/useTranslations'
import {useQueryClient} from '@tanstack/react-query'
import {useMutationFollowUnfollow} from '@hook/query/timeline/useMutationFollowUnfollow'
import useQueryGetUserSidebar from '@hook/query/portfolio/useQueryGetUserSidebar'

import WizButton from '@component/buttons/WizButton'
import CreateCopyModal from '@feature/copy/components/CreateCopyModal/CreateCopyModal'

interface IProps {
    className?: string
    id?: string
    login_id?: string
}

const MobileUserProfileOperations = ({className, id, login_id}: IProps) => {
    const t = useTranslation()
    const router = useRouter()
    const queryClient = useQueryClient()

    const createCopyModalRef = useRef<any>()

    const {data: sidebar, refetch} = useQueryGetUserSidebar(id, false)
    const {mutateAsync} = useMutationFollowUnfollow()

    const onRefresh = async () => {
        await refetch()
        await queryClient.refetchQueries(QueryCacheKeys.portfolio.getUserProfile(id, router?.locale))
    }

    const onClickCopy = () => {
        createLog('event', 'portfolio_user_profile_header_open_copy_modal')
        createCopyModalRef.current?.show()
    }

    const onClickPosts = () => {
        createLog('event', 'portfolio_user_profile_header_click_posts')
        void router.push(getLinkUrl(URL_TIMELINE_USER(id)))
    }

    const onClickFollowing = async () => {
        createLog(
            'event',
            sidebar?.is_following
                ? 'portfolio_user_profile_header_click_unfollow'
                : 'portfolio_user_profile_header_click_follow',
        )
        await mutateAsync({isFollowing: sidebar?.is_following, user_id: id}, {onSuccess: onRefresh})
    }

    // 내 포트폴리오 (거래소 연결)
    if (id === login_id) {
        return (
            <Link href={getLinkUrl(URL_ACCOUNT_API)}>
                <WizButton
                    text={t('portfolio.user.profile.exchanges.button.text')}
                    size={'small'}
                    buttonType={'border'}
                    className={'w-full mt-[7px]'}
                    isLink
                />
            </Link>
        )
    }
    // 다른 사용자 포트폴리오 (카피, 타임라인, 구독)
    return (
        <div className={`flex items-center gap-x-[5px] ${className}`}>
            <WizButton
                disabled={!sidebar?.can_copy}
                text={t('portfolio.sidebar.copy.title')}
                size={'small'}
                buttonType={'border'}
                className={'w-full'}
                onClick={onClickCopy}
            />
            <WizButton
                text={t('timeline.item.header.posts.button.text')}
                size={'small'}
                buttonType={'border'}
                className={'w-full'}
                onClick={onClickPosts}
            />
            <WizButton
                text={
                    sidebar?.is_following
                        ? t('portfolio_sidebar_unsubscribe_title')
                        : t('portfolio_sidebar_subscribe_title')
                }
                size={'small'}
                buttonType={sidebar?.is_following ? 'border' : 'primary'}
                className={'w-full'}
                onClick={onClickFollowing}
            />
            <CreateCopyModal ref={createCopyModalRef} leaderUserId={id} />
        </div>
    )
}
export default MobileUserProfileOperations
